import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Box,
    CircularProgress,
    Alert,
    useTheme,
    Modal,
    IconButton,
    TextField,
    InputAdornment,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    List,
    ListItem,
    ListItemText,
    Chip,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    Security as SecurityIcon,
    Shield as ShieldIcon,
    Lock as LockIcon,
    Verified as VerifiedIcon,
    ArrowForward,
    Close as CloseIcon,
    CreditCard as CreditCardIcon,
    Info as InfoIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { Plan, PaymentRequestData, SavedCreditCard } from '../types/plan';
import { fetchPlans, processPayment, changePlan } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Organization } from '../utils/organizationApi';
import PaymentLoadingModal from '../components/PaymentLoadingModal';
import { api } from '../utils/api';

// Keyframes for animations
const pulseGlow = keyframes`
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(53, 232, 124, 0.7);
    }
    50% {
        transform: scale(1.15);
        box-shadow: 0 0 8px 4px rgba(53, 232, 124, 0.4);
    }
`;

const colorPulse = keyframes`
    0%, 100% {
        color: #35e87c;
        background: rgba(53, 232, 124, 0.15);
    }
    50% {
        color: #22c55e;
        background: rgba(34, 197, 94, 0.25);
    }
`;

// Styled Components
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    width: '100vw',
    maxWidth: '100%',
    overflowX: 'hidden',
    background: theme.palette.mode === 'dark'
        ? '#0a0a0a'
        : '#ffffff',
    transition: 'background 0.3s ease',
}));

const LogoImage = styled('img')(({ theme }) => ({
    width: '180px',
    height: 'auto',
    marginBottom: theme.spacing(6),
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

const PlansBoxContainer = styled(Box)(({ theme }) => ({
    maxWidth: '680px',
    margin: '0 auto',
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : '#ffffff',
    borderRadius: '24px',
    padding: theme.spacing(4),
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
        : '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,

    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(5),
    },
}));

const PlanCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    borderRadius: '16px',
    cursor: 'pointer',
    border: `2px solid ${isSelected
        ? '#35e87c'
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
        }`,
    background: isSelected
        ? (theme.palette.mode === 'dark' ? 'rgba(53, 232, 124, 0.08)' : 'rgba(53, 232, 124, 0.05)')
        : 'transparent',
    transition: 'all 0.2s ease',
    marginBottom: theme.spacing(2),

    '&:last-child': {
        marginBottom: 0,
    },

    '&:hover': {
        border: `2px solid ${isSelected
            ? '#35e87c'
            : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)')
            }`,
        background: isSelected
            ? (theme.palette.mode === 'dark' ? 'rgba(53, 232, 124, 0.1)' : 'rgba(53, 232, 124, 0.08)')
            : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)'),
    },

    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: theme.spacing(2.5, 4),
        gap: theme.spacing(2),
    },
}));

const PlanLeftSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 0,
}));

const PlanRadio = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    flexShrink: 0,
}));

const PlanInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,

    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
}));

const PlanName = styled(Typography)(({ theme }) => ({
    fontSize: '1.25rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,

    [theme.breakpoints.up('md')]: {
        fontSize: '1.5rem',
    },
}));

const PlanBadges = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const Badge = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'none',
}));

const SaveBadge = styled(Badge)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
        ? 'rgba(34, 197, 94, 0.2)'
        : 'rgba(34, 197, 94, 0.15)',
    color: theme.palette.mode === 'dark'
        ? '#4ade80'
        : '#16a34a',
}));

const PromoBadge = styled(Badge)(({ theme }) => ({
    position: 'absolute',
    top: '-10px',
    right: theme.spacing(2),
    background: theme.palette.mode === 'dark'
        ? '#4ade80'
        : '#22c55e',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '0.7rem',
    padding: '4px 10px',
}));

const CancelledBadge = styled(Badge)(({ theme }) => ({
    position: 'absolute',
    top: '-10px',
    right: theme.spacing(2),
    background: '#ef4444',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '0.7rem',
    padding: '4px 10px',
}));

const PriceSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),

    [theme.breakpoints.up('md')]: {
        alignItems: 'flex-end',
        marginTop: 0,
        minWidth: '180px',
    },
}));

const PriceRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
}));

const OldPrice = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textDecoration: 'line-through',
}));

const Price = styled(Typography)(({ theme }) => ({
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,

    [theme.breakpoints.up('md')]: {
        fontSize: '2.5rem',
    },
}));

const PriceDescription = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
}));

const ContinueSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    textAlign: 'center',
}));

const ContinueButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.75, 5),
    borderRadius: '12px',
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.95)'
        : '#000000',
    color: theme.palette.mode === 'dark'
        ? '#000000'
        : '#ffffff',
    transition: 'all 0.2s ease',

    '&:hover': {
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : '#000000',
        transform: 'translateY(-2px)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(255, 255, 255, 0.15)'
            : '0 8px 24px rgba(0, 0, 0, 0.2)',
    },
}));

const SecuritySection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(8),
    padding: theme.spacing(4),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
}));

const SecurityFeatures = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
}));

const SecurityFeature = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const ReviewSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(12),
    marginBottom: theme.spacing(8),
}));

const ReviewCard = styled(Box)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : '#ffffff',
    borderRadius: '16px',
    padding: theme.spacing(5),
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
        : '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
}));

const ReviewText = styled(Typography)(({ theme }) => ({
    fontSize: '1.125rem',
    lineHeight: 1.7,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(4),
}));

const ReviewAuthor = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const AuthorAvatar = styled('img')(({ theme }) => ({
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    objectFit: 'cover',
}));

const VideoSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(12),
    marginBottom: theme.spacing(12),
}));

const VideoContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    paddingBottom: '56.25%', // 16:9 aspect ratio
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
        : '0 8px 32px rgba(0, 0, 0, 0.12)',
}));

const FeaturesSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(12),
    padding: theme.spacing(8, 0),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.02)',
    borderRadius: '32px',
}));

const FeatureCategory = styled(Box)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : '#ffffff',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
    breakInside: 'avoid',
}));

const FeatureCategoryHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
}));

const FeatureCategoryTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.25rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
}));

const FeatureList = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
}));

const FeatureItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    '&:last-child': {
        marginBottom: 0,
    },
}));

const FeatureItemText = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
}));

// Payment Modal Styles
const PaymentModalBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '680px',
    maxHeight: '90vh',
    overflow: 'auto',
    background: theme.palette.mode === 'dark'
        ? 'rgba(10, 10, 10, 0.98)'
        : '#ffffff',
    borderRadius: '24px',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 24px 48px rgba(0, 0, 0, 0.6)'
        : '0 24px 48px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    padding: theme.spacing(4),
    outline: 'none',

    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(5),
    },
}));

const PaymentModalHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    '&:hover': {
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.05)',
    },
}));

const PaymentFormBox = styled(Box)(({ theme }) => ({
    '& .MuiTextField-root': {
        marginBottom: theme.spacing(2.5),
    },
}));

const PlanSummaryBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    marginBottom: theme.spacing(3),
}));

const PayButton = styled(Button)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(1.75, 3),
    borderRadius: '12px',
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.95)'
        : '#000000',
    color: theme.palette.mode === 'dark'
        ? '#000000'
        : '#ffffff',
    transition: 'all 0.2s ease',

    '&:hover': {
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 1)'
            : '#000000',
        transform: 'translateY(-2px)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(255, 255, 255, 0.15)'
            : '0 8px 24px rgba(0, 0, 0, 0.2)',
    },

    '&:disabled': {
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(0, 0, 0, 0.2)',
        color: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(255, 255, 255, 0.5)',
    },
}));

// Countdown Timer Styles
const CountdownContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '56px',
    padding: theme.spacing(2),
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(to right, rgba(74, 222, 128, 0.15), rgba(34, 197, 94, 0.1))'
        : 'linear-gradient(to right, rgba(34, 197, 94, 0.1), rgba(74, 222, 128, 0.15))',
    borderRadius: '16px',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(6),
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(34, 197, 94, 0.2)'}`,

    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        height: '56px',
        padding: theme.spacing(2, 4),
        gap: 0,
    },
}));

const CountdownMessage = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const CountdownTimer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),

    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        gap: theme.spacing(1),
    },
}));

const TimerDisplay = styled(Typography)(({ theme }) => ({
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,

    [theme.breakpoints.up('md')]: {
        fontSize: '2.5rem',
    },
}));

// Discount Countdown Styles
const DiscountCountdownContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '56px',
    padding: theme.spacing(2),
    background: theme.palette.mode === 'dark'
        ? 'rgba(34, 197, 94, 0.2)'
        : 'rgba(187, 247, 208, 1)',
    borderRadius: '16px',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(6),
    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.6)' : '#22c55e'}`,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(34, 197, 94, 0.2)'
        : '0 8px 24px rgba(34, 197, 94, 0.15)',

    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        height: '56px',
        padding: theme.spacing(2, 3),
        gap: 0,
    },
}));

// Info Button in Plan Card with pulsing animation
const InfoButton = styled(IconButton)(({ theme }) => ({
    width: '32px',
    height: '32px',
    padding: 0,
    color: '#35e87c',
    background: theme.palette.mode === 'dark'
        ? 'rgba(53, 232, 124, 0.15)'
        : 'rgba(53, 232, 124, 0.12)',
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
    transition: 'all 0.3s ease',
    animation: `${pulseGlow} 2s ease-in-out infinite, ${colorPulse} 2s ease-in-out infinite`,
    border: `2px solid rgba(53, 232, 124, 0.3)`,

    '&:hover': {
        background: theme.palette.mode === 'dark'
            ? 'rgba(53, 232, 124, 0.25)'
            : 'rgba(53, 232, 124, 0.2)',
        color: '#22c55e',
        transform: 'scale(1.2) rotate(5deg)',
        boxShadow: '0 0 12px 4px rgba(53, 232, 124, 0.5)',
        animation: 'none',
        border: `2px solid rgba(53, 232, 124, 0.6)`,
    },
}));

// Plan Details Modal Styles
const PlanDetailsModalBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    background: theme.palette.mode === 'dark'
        ? 'rgba(10, 10, 10, 0.98)'
        : '#ffffff',
    borderRadius: '24px',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 24px 48px rgba(0, 0, 0, 0.6)'
        : '0 24px 48px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    padding: theme.spacing(4),
    outline: 'none',

    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(5),
    },
}));

const PlanDetailsHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const PlanFeaturesList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const PlanFeatureItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',

    '&:hover': {
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.04)',
    },
}));

interface PlanSelectionPageProps {
    onPlanSelect: (plan: Plan) => void;
    onBack: () => void;
    isPlanChange?: boolean;
    forceSelection?: boolean;
    currentPlanId?: string;
    subscriptionActive?: boolean;
    signUpData?: any; // Dados do formulário de cadastro para criar conta após pagamento
}

const PlanSelectionPage: React.FC<PlanSelectionPageProps> = ({
    onPlanSelect,
    onBack,
    isPlanChange = false,
    forceSelection = false,
    currentPlanId,
    subscriptionActive = true,
    signUpData
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user, signUp, logout, refreshCurrentOrganization } = useAuth();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    // Countdown Timer State (10 minutes = 600 seconds)
    const [timeLeft, setTimeLeft] = useState(600);

    // Discount State - triggers when user closes modal without payment
    const [showDiscount, setShowDiscount] = useState(false);
    const [hasOpenedPaymentModal, setHasOpenedPaymentModal] = useState(false);

    // Plan Details Modal States
    const [planDetailsModalOpen, setPlanDetailsModalOpen] = useState(false);
    const [selectedPlanForDetails, setSelectedPlanForDetails] = useState<Plan | null>(null);

    // Payment Modal States
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [showPaymentLoading, setShowPaymentLoading] = useState(false);
    const [paymentLoadingMessage, setPaymentLoadingMessage] = useState('Processando pagamento...');
    const [cardNumber, setCardNumber] = useState('');
    const [cardHolder, setCardHolder] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [securityCode, setSecurityCode] = useState('');
    const [customerName, setCustomerName] = useState(user?.name || '');
    const [customerEmail, setCustomerEmail] = useState(user?.email || '');
    const [customerIdentity, setCustomerIdentity] = useState('');
    const [savedCards, setSavedCards] = useState<SavedCreditCard[]>([]);
    const [useNewCard, setUseNewCard] = useState(true);
    const [selectedCardId, setSelectedCardId] = useState<string>('');

    const loadPlans = useCallback(async () => {
        try {
            setLoading(true);
            const plansData = await fetchPlans();

            if (isPlanChange) {
                const filteredPlans = plansData.filter(plan => {
                    if (plan.duration === 'free') {
                        return false;
                    }
                    if (subscriptionActive && currentPlanId && plan.id === currentPlanId) {
                        return false;
                    }
                    if (!subscriptionActive && currentPlanId && plan.id === currentPlanId) {
                        return true;
                    }
                    return true;
                });
                setPlans(filteredPlans);
            } else {
                setPlans(plansData);
            }
        } catch (err) {
            setError('Erro ao carregar planos. Tente novamente.');
            console.error('Error loading plans:', err);
        } finally {
            setLoading(false);
        }
    }, [isPlanChange, currentPlanId, subscriptionActive]);

    useEffect(() => {
        loadPlans();
    }, [loadPlans]);

    // Auto-select Start plan when plans are loaded (only once)
    useEffect(() => {
        if (plans.length > 0 && !selectedPlan && !isPlanChange) {
            const startPlan = plans.find(plan => plan.name.toLowerCase().includes('start'));
            if (startPlan) {
                setSelectedPlan(startPlan);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plans]); // Removido selectedPlan das dependências para rodar apenas quando plans carregar

    // Countdown Timer Effect
    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    // Format time as MM:SS
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handlePlanSelect = (plan: Plan) => {
        console.log('Plano selecionado pelo usuário:', plan.name, 'ID:', plan.id);
        setSelectedPlan(plan);
    };

    const handleContinue = async () => {
        if (selectedPlan) {
            console.log('[PlanSelectionPage] handleContinue chamado');
            console.log('[PlanSelectionPage] Plano selecionado:', selectedPlan.name, 'ID:', selectedPlan.id);
            console.log('[PlanSelectionPage] signUpData:', signUpData);
            console.log('[PlanSelectionPage] forceSelection:', forceSelection);
            console.log('[PlanSelectionPage] isPlanChange:', isPlanChange);
            console.log('[PlanSelectionPage] user:', user);

            // Se o plano for gratuito, ativar o plano
            if (selectedPlan.duration === 'free') {
                console.log('[PlanSelectionPage] Plano gratuito detectado');
                console.log('[PlanSelectionPage] user:', user);
                console.log('[PlanSelectionPage] signUpData:', signUpData);
                
                // Se o usuário já está autenticado (conta já foi criada), usar changePlan
                if (user) {
                    console.log('[PlanSelectionPage] Usuário autenticado - usando changePlan');
                    setLoading(true);
                    try {
                        const changePlanData: any = {
                            planId: selectedPlan.id,
                            customerName: user.name || 'Cliente',
                            customerEmail: user.email || 'cliente@example.com',
                            customerIdentity: '00000000000',
                            customerIdentityType: 'CPF'
                        };
                        
                        const response = await changePlan(changePlanData);
                        
                        if (response.success) {
                            console.log('[PlanSelectionPage] Plano gratuito ativado com sucesso');
                            
                            // Atualizar organização no contexto
                            if (response.organization) {
                                localStorage.setItem('currentOrganization', JSON.stringify(response.organization));
                            }
                      
                            // Redirecionar para /admin após pagamento bem-sucedido
                            console.log('[PlanSelectionPage] Pagamento aprovado - redirecionando para /admin');
                            localStorage.removeItem('needsPlanSelection');
                            
                            // Usar window.location.href para forçar reload completo e atualizar o contexto
                            window.location.href = '/admin';
                        } else {
                            setError(response.message || 'Erro ao ativar plano gratuito');
                            setLoading(false);
                        }
                    } catch (error: any) {
                        console.error('[PlanSelectionPage] Erro ao ativar plano gratuito:', error);
                        setError(error.message || 'Erro ao ativar plano gratuito');
                        setLoading(false);
                    }
                } 
                // Se tem dados de signup mas usuário NÃO está autenticado (ainda não criou conta), criar conta diretamente
                else if (signUpData && signUp) {
                    console.log('[PlanSelectionPage] Criando conta com plano gratuito');
                    setLoading(true);
                    try {
                        const result = await signUp({
                            ...signUpData,
                            planId: selectedPlan.id
                        });

                        if (result.success) {
                            navigate('/admin', { state: { showWelcome: true } });
                        } else {
                            setError(result.error || 'Erro ao criar conta');
                            setLoading(false);
                        }
                    } catch (signUpError: any) {
                        console.error('Error creating account:', signUpError);
                        setError(signUpError.message || 'Erro ao criar conta');
                        setLoading(false);
                    }
                } 
                // Se não tem dados de signup e não está autenticado, usar callback
                else {
                    console.log('[PlanSelectionPage] Plano gratuito - chamando callback');
                    onPlanSelect(selectedPlan);
                }
                return;
            }

            // Para planos pagos, SEMPRE abrir modal de pagamento
            console.log('[PlanSelectionPage] Abrindo modal de pagamento');
            setPaymentError(null); // Limpa erros anteriores
            // Carregar cartões salvos apenas se usuário estiver autenticado
            if (user) {
                console.log('[PlanSelectionPage] Carregando cartões salvos');
                loadSavedCards(); // Carregar cartões salvos quando abrir modal
            }
            setPaymentModalOpen(true);
            setHasOpenedPaymentModal(true);
            console.log('[PlanSelectionPage] Modal de pagamento aberta');
        }
    };

    const loadSavedCards = async () => {
        // Não carregar cartões salvos durante o processo de signup (usuário não está autenticado)
        if (signUpData || !user) {
            setUseNewCard(true);
            return;
        }

        try {
            const cards = await api.get<SavedCreditCard[]>('/Payment/GetSavedCards', { auth: true });
            setSavedCards(cards);
            
            // Se há cartões salvos, selecionar o padrão ou o primeiro
            if (cards.length > 0) {
                const defaultCard = cards.find((c: SavedCreditCard) => c.isDefault);
                const cardToSelect = defaultCard || cards[0];
                setSelectedCardId(cardToSelect.id);
                setUseNewCard(false); // Usar cartão salvo por padrão se existir
                
                // Preencher automaticamente os dados do cliente se disponíveis
                if (cardToSelect.customerCpfCnpj || cardToSelect.customerName || cardToSelect.customerEmail) {
                    setCustomerIdentity(cardToSelect.customerCpfCnpj || customerIdentity);
                    setCustomerName(cardToSelect.customerName || customerName);
                    setCustomerEmail(cardToSelect.customerEmail || customerEmail);
                }
            } else {
                setUseNewCard(true); // Usar novo cartão se não houver cartões salvos
            }
        } catch (error) {
            console.error('Erro ao carregar cartões salvos:', error);
            setUseNewCard(true);
        }
    };

    const handleDeleteCard = async (cardId: string) => {
        try {
            await api.delete(`/Payment/DeleteSavedCard/${cardId}`, { auth: true });
            loadSavedCards();
        } catch (error) {
            console.error('Erro ao deletar cartão:', error);
        }
    };

    const handleClosePaymentModal = () => {
        setPaymentModalOpen(false);
        setPaymentError(null); // Limpa erros ao fechar modal

        // Se o usuário fechou a modal sem pagar e tem um plano selecionado que não é free
        if (hasOpenedPaymentModal && selectedPlan && selectedPlan.duration !== 'free') {
            // setShowDiscount(true);
        }
    };

    const handleOpenPlanDetails = (plan: Plan, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedPlanForDetails(plan);
        setPlanDetailsModalOpen(true);
    };

    const handleClosePlanDetails = () => {
        setPlanDetailsModalOpen(false);
        setSelectedPlanForDetails(null);
    };

    const formatPrice = (price: number) => {
        return price.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const formatCardNumber = (value: string) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        const matches = v.match(/\d{4,16}/g);
        const match = (matches && matches[0]) || '';
        const parts = [];

        for (let i = 0, len = match.length; i < len; i += 4) {
            parts.push(match.substring(i, i + 4));
        }

        if (parts.length) {
            return parts.join(' ');
        } else {
            return value;
        }
    };

    const formatExpirationDate = (value: string) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        if (v.length >= 2) {
            // Limitar mês a 12
            const month = Math.min(parseInt(v.slice(0, 2)) || 0, 12);
            const monthStr = month.toString().padStart(2, '0');
            if (v.length >= 4) {
                return monthStr + '/' + v.slice(2, 4);
            }
            return monthStr + (v.length > 2 ? '/' + v.slice(2) : '');
        }
        return v;
    };

    // Validação do número do cartão usando algoritmo de Luhn
    const validateCardNumber = (cardNumber: string): boolean => {
        const cleaned = cardNumber.replace(/\s/g, '');
        if (!cleaned || cleaned.length < 13 || cleaned.length > 19) {
            return false;
        }

        let sum = 0;
        let isEven = false;

        // Percorrer de trás para frente
        for (let i = cleaned.length - 1; i >= 0; i--) {
            let digit = parseInt(cleaned.charAt(i));

            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }

            sum += digit;
            isEven = !isEven;
        }

        return sum % 10 === 0;
    };

    // Validação da data de vencimento
    const validateExpirationDate = (expirationDate: string): { valid: boolean; message?: string } => {
        if (!expirationDate || expirationDate.length !== 5) {
            return { valid: false, message: 'Data de validade inválida' };
        }

        const parts = expirationDate.split('/');
        if (parts.length !== 2) {
            return { valid: false, message: 'Data de validade inválida' };
        }

        const month = parseInt(parts[0]);
        const year = parseInt(parts[1]);

        // Validar mês
        if (isNaN(month) || month < 1 || month > 12) {
            return { valid: false, message: 'Mês inválido. Use um valor entre 01 e 12' };
        }

        // Validar ano
        if (isNaN(year) || year < 0 || year > 99) {
            return { valid: false, message: 'Ano inválido' };
        }

        // Converter para data completa (assumindo 2000-2099)
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear() % 100;
        const currentMonth = currentDate.getMonth() + 1;

        // Converter ano de 2 dígitos para 4 dígitos
        const fullYear = year < 50 ? 2000 + year : 1900 + year;

        // Verificar se a data não está expirada
        if (fullYear < currentDate.getFullYear() || 
            (fullYear === currentDate.getFullYear() && month < currentMonth)) {
            return { valid: false, message: 'Cartão expirado. Verifique a data de vencimento' };
        }

        return { valid: true };
    };

    const formatCPF = (value: string) => {
        const v = value.replace(/\D/g, '').slice(0, 11); // Limita a 11 dígitos

        if (v.length <= 3) {
            return v;
        } else if (v.length <= 6) {
            return `${v.slice(0, 3)}.${v.slice(3)}`;
        } else if (v.length <= 9) {
            return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
        } else {
            return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9, 11)}`;
        }
    };

    const validateCPF = (cpf: string) => {
        cpf = cpf.replace(/[^\d]+/g, '');
        if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let rev = 11 - (sum % 11);
        if (rev === 10 || rev === 11) rev = 0;
        if (rev !== parseInt(cpf.charAt(9))) return false;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        rev = 11 - (sum % 11);
        if (rev === 10 || rev === 11) rev = 0;
        if (rev !== parseInt(cpf.charAt(10))) return false;

        return true;
    };

    const validatePaymentForm = () => {
        if (!useNewCard && !selectedCardId) {
            setPaymentError('Selecione um cartão salvo ou use um novo cartão');
            return false;
        }
        if (useNewCard) {
            if (!cardNumber || !validateCardNumber(cardNumber)) {
                setPaymentError('Número do cartão inválido. Verifique o número do cartão');
                return false;
            }
            if (!cardHolder || cardHolder.length < 3) {
                setPaymentError('Nome do titular inválido');
                return false;
            }
            const expirationValidation = validateExpirationDate(expirationDate);
            if (!expirationValidation.valid) {
                setPaymentError(expirationValidation.message || 'Data de validade inválida');
                return false;
            }
            if (!securityCode || securityCode.length < 3 || securityCode.length > 4) {
                setPaymentError('CVV inválido (deve ter 3 ou 4 dígitos)');
                return false;
            }
        }
        if (!customerName || customerName.length < 3) {
            setPaymentError('Nome do cliente inválido');
            return false;
        }
        if (!customerEmail || !customerEmail.includes('@')) {
            setPaymentError('E-mail inválido');
            return false;
        }
        if (!customerIdentity || !validateCPF(customerIdentity)) {
            setPaymentError('CPF inválido');
            return false;
        }
        setPaymentError(null);
        return true;
    };

    const handlePayment = async () => {
        if (!validatePaymentForm()) return;

        // Garantir que o planId está correto antes de enviar
        if (!selectedPlan) {
            setPaymentError('Nenhum plano selecionado');
            return;
        }

        // Montar dados de pagamento na nova estrutura
        const [expiryMonth, expiryYear] = expirationDate.split('/');
        const paymentDataToSend: PaymentRequestData = {
            planId: selectedPlan.id,
            paymentType: isPlanChange ? 'PLAN_CHANGE' : 'PLAN',
            creditCardToken: useNewCard ? undefined : selectedCardId,
            creditCard: useNewCard ? {
                holderName: cardHolder,
                number: cardNumber.replace(/\s/g, ''),
                expiryMonth: expiryMonth,
                expiryYear: expiryYear,
                ccv: securityCode
            } : undefined,
            // Sempre salvar cartão quando usar novo cartão (o backend sempre salva automaticamente)
            saveCard: useNewCard ? true : false,
            customer: {
                name: customerName,
                email: customerEmail,
                cpfCnpj: customerIdentity.replace(/\D/g, '')
            }
        };

        console.log('Enviando pagamento para plano:', selectedPlan.name, 'ID:', selectedPlan.id);
        console.log('Payment Data:', paymentDataToSend);

        setShowPaymentLoading(true);
        setPaymentLoadingMessage('Processando pagamento de forma segura...');

        try {
            // Nota: Se tem signUpData, o fluxo agora vai para PaymentPage separada
            // então não deve entrar aqui. A lógica antiga foi removida.
            
            // Se usuário está autenticado (mudança de plano OU forceSelection após signup), usar changePlan API
            if (isPlanChange || forceSelection) {
                setPaymentLoadingMessage(forceSelection ? 'Processando pagamento...' : 'Processando alteração de plano...');
                
                // Converter para formato ChangePlanRQ se necessário
                const [expiryMonth, expiryYear] = expirationDate.split('/');
                const changePlanData: any = {
                    planId: selectedPlan.id,
                    cardNumber: useNewCard ? cardNumber.replace(/\s/g, '') : '',
                    cardHolder: useNewCard ? cardHolder : '',
                    expirationDate: useNewCard ? expirationDate : '',
                    securityCode: useNewCard ? securityCode : '',
                    brand: 'visa', // TODO: Detectar bandeira automaticamente
                    customerName: customerName,
                    customerEmail: customerEmail,
                    customerIdentity: customerIdentity.replace(/\D/g, ''),
                    customerIdentityType: 'CPF',
                    // Se usar cartão salvo, enviar o ID do cartão (o backend busca o token)
                    creditCardToken: useNewCard ? undefined : selectedCardId
                };
                
                const response = await changePlan(changePlanData);

                console.log('[PlanSelectionPage] Resposta do changePlan:', response);
                console.log('[PlanSelectionPage] response.success:', response.success);

                if (response.success) {
                    console.log('[PlanSelectionPage] ENTROU NO IF response.success');
                    console.log('[PlanSelectionPage] forceSelection:', forceSelection);
                    console.log('[PlanSelectionPage] isPlanChange:', isPlanChange);
                    
                    setPaymentLoadingMessage(forceSelection ? 'Pagamento aprovado!' : 'Plano alterado com sucesso!');

                    // Atualizar a organização no contexto com os dados retornados do backend
                    if (response.organization) {
                        console.log('[PlanSelectionPage] Atualizando organização no localStorage');
                        localStorage.setItem('currentOrganization', JSON.stringify(response.organization));
                    }

                    // Verificar se o plano alterado é WhiteLabel
                    const isWhiteLabelPlan = selectedPlan?.id === 'whitelabel' ||
                        selectedPlan?.name?.toLowerCase().includes('white label') ||
                        selectedPlan?.name?.toLowerCase().includes('whitelabel');

                    console.log('[PlanSelectionPage] isWhiteLabelPlan:', isWhiteLabelPlan);
                    console.log('[PlanSelectionPage] selectedPlan:', selectedPlan);

                    // Aguardar 1 segundo para mostrar mensagem de sucesso na modal antes de fechar
                    setTimeout(() => {
                        console.log('[PlanSelectionPage] Dentro do setTimeout - fechando modal');
                        setShowPaymentLoading(false);
                        setPaymentModalOpen(false);

                        // Se for plano WhiteLabel, fazer logout
                        if (isWhiteLabelPlan) {
                            console.log('[PlanSelectionPage] Plano WhiteLabel detectado, fazendo logout');
                            logout();
                            navigate('/login');
                        } else {
                            // Sempre redirecionar para /admin após pagamento bem-sucedido (forceSelection ou isPlanChange)
                            console.log('[PlanSelectionPage] Pagamento bem-sucedido - redirecionando para /admin');
                            localStorage.removeItem('needsPlanSelection');
                            
                            // Chamar callback se existir
                            if (onPlanSelect) {
                                onPlanSelect(selectedPlan!);
                            }
                            
                            // Usar window.location.href para forçar reload completo e atualizar o contexto
                            window.location.href = '/admin';
                        }
                    }, 1000);
                } else {
                    console.log('[PlanSelectionPage] Erro ao alterar plano:', response.message);
                    setShowPaymentLoading(false);
                    setPaymentError(response.message || 'Erro ao alterar plano');
                }
            }
            // Fallback para outros casos (não deveria acontecer quando usuário está autenticado)
            else {
                const response = await processPayment(paymentDataToSend);

                if (response.status === 'Success') {
                    setPaymentLoadingMessage('Pagamento aprovado!');
                    
                    // Atualizar a organização no contexto se vier no response
                    if (response.organization) {
                        console.log('[PlanSelectionPage] Atualizando organização com dados do pagamento:', response.organization);
                        // Mapear a organização do response para o formato esperado
                        const updatedOrg: Organization = {
                            id: response.organization.id,
                            name: response.organization.name,
                            identifier: response.organization.identifier,
                            credit: response.organization.credit,
                            currentPlan: response.organization.currentPlan,
                            endIn: response.organization.endIn,
                            subscriptionActive: response.organization.subscriptionActive,
                            permissions: response.organization.permissions,
                            active: response.organization.active,
                            isWhiteLabelManaged: response.organization.isWhiteLabelManaged,
                            whiteLabelUserId: response.organization.whiteLabelUserId,
                            createdAt: response.organization.createdAt,
                            updatedAt: response.organization.updatedAt
                        };
                        // Atualizar no localStorage
                        localStorage.setItem('currentOrganization', JSON.stringify(updatedOrg));
                        // Chamar refreshCurrentOrganization para atualizar o contexto
                        if (refreshCurrentOrganization) {
                            await refreshCurrentOrganization();
                        }
                    }
                    
                    // Verificar se o plano alterado é WhiteLabel
                    const isWhiteLabelPlan = selectedPlan?.id === 'whitelabel' ||
                        selectedPlan?.name?.toLowerCase().includes('white label') ||
                        selectedPlan?.name?.toLowerCase().includes('whitelabel');

                    setTimeout(() => {
                        setShowPaymentLoading(false);
                        setPaymentModalOpen(false);
                        
                        if (isWhiteLabelPlan) {
                            console.log('[PlanSelectionPage] Plano WhiteLabel detectado, fazendo logout');
                            logout();
                            navigate('/login');
                        } else {
                            // Redirecionar para /admin após pagamento bem-sucedido
                            console.log('[PlanSelectionPage] Pagamento aprovado - redirecionando para /admin');
                            localStorage.removeItem('needsPlanSelection');
                            
                            // Usar window.location.href para forçar reload completo e atualizar o contexto
                            window.location.href = '/admin';
                        }
                    }, 1500);
                } else {
                    setShowPaymentLoading(false);
                    setPaymentError(response.message || 'Erro ao processar pagamento');
                }
            }
        } catch (err: any) {
            setShowPaymentLoading(false);
            setPaymentError(err.message || 'Erro ao processar pagamento. Tente novamente.');
        }
    };

    const calculateSavings = (currentPrice: number, originalPrice: number) => {
        if (originalPrice <= currentPrice) return null;
        const savings = ((originalPrice - currentPrice) / originalPrice * 100);
        return Math.round(savings);
    };

    // Apply 10% discount to price when discount is active
    const getPriceWithDiscount = (price: number) => {
        if (showDiscount) {
            return price * 0.90; // 10% off
        }
        return price;
    };

    // Get plan features based on plan type
    const getPlanFeatures = (plan: Plan) => {
        const features = [
            `${plan.initialCredits.toLocaleString()} créditos iniciais`,
            `Até ${plan.maxMedia === -1 ? '∞' : plan.maxMedia} mídias nos prompts`,
        ];

        if (plan.duration === 'free') {
            features.push('3 meses de teste');
            features.push('Funcionalidades básicas');
            features.push('Suporte por email');
        } else {
            features.push('Acesso completo à plataforma');
            features.push('Suporte prioritário');
            features.push('Relatórios avançados');
            features.push('Integração WhatsApp');
            features.push('API de Integração');

            if (plan.name.toLowerCase().includes('professional') ||
                plan.name.toLowerCase().includes('enterprise') ||
                plan.name.toLowerCase().includes('white')) {
                features.push('Dashboard personalizado');
                features.push('Automação de follow-up');
                features.push('Análise de performance detalhada');
                features.push('Webhooks personalizados');
            }

            if (plan.name.toLowerCase().includes('enterprise') ||
                plan.name.toLowerCase().includes('white')) {
                features.push('API dedicada');
                features.push('SLA garantido');
                features.push('Onboarding personalizado');
                features.push('Account manager dedicado');
            }

            if (plan.name.toLowerCase().includes('white')) {
                features.push('Marca personalizada (White Label)');
                features.push('Revenda de créditos');
                features.push('Gestão multi-organização');
                features.push('Domínio personalizado');
            }
        }

        return features;
    };

    if (loading) {
        return (
            <PageWrapper>
                <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
                    <CircularProgress size={60} sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }} />
                    <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
                        Carregando planos...
                    </Typography>
                </Container>
            </PageWrapper>
        );
    }

    if (error) {
        return (
            <PageWrapper>
                <Container maxWidth="lg" sx={{ py: 8 }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                    <Button variant="outlined" onClick={loadPlans}>
                        Tentar novamente
                    </Button>
                </Container>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <Container maxWidth="lg" sx={{ py: 6 }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <LogoImage
                        src={theme.palette.mode === 'dark' ? "/logo-branco.svg" : "/logo-color.svg"}
                        alt="LIA TALK Logo"
                    />
                </Box>

                {/* Countdown Timer - Normal or Discount */}
                {!isPlanChange && timeLeft > 0 && !showDiscount && (
                    <CountdownContainer>
                        <CountdownMessage>
                            <CheckCircleIcon sx={{
                                fontSize: { xs: 24, md: 28 },
                                color: theme.palette.mode === 'dark' ? '#4ade80' : '#22c55e',
                            }} />
                            <Typography
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                    fontWeight: 700,
                                    color: 'text.primary',
                                }}
                            >
                                Seu perfil está completo!
                            </Typography>
                        </CountdownMessage>

                        <CountdownTimer>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                }}
                            >
                                Selecione um plano para ativar em
                            </Typography>
                            <TimerDisplay>
                                {formatTime(timeLeft)}
                            </TimerDisplay>
                        </CountdownTimer>
                    </CountdownContainer>
                )}

                {/* Discount Countdown Timer */}
                {!isPlanChange && timeLeft > 0 && showDiscount && (
                    <DiscountCountdownContainer>
                        <CountdownMessage>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                }}
                            >
                                🎁
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                    fontWeight: 700,
                                    color: theme.palette.mode === 'dark' ? 'text.primary' : '#0f172a',
                                }}
                            >
                                Ganhe 10% de desconto!
                            </Typography>
                        </CountdownMessage>

                        <CountdownTimer>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    color: theme.palette.mode === 'dark' ? 'text.primary' : '#0f172a',
                                }}
                            >
                                Ative em
                            </Typography>
                            <TimerDisplay sx={{
                                color: theme.palette.mode === 'dark' ? 'text.primary' : '#0f172a',
                            }}>
                                {formatTime(timeLeft)}
                            </TimerDisplay>
                        </CountdownTimer>
                    </DiscountCountdownContainer>
                )}

                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', md: '3rem' },
                            mb: 2,
                            color: 'text.primary',
                        }}
                    >
                        {isPlanChange ? 'Alterar Plano' : 'Pronto para começar?'}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 400,
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            maxWidth: 600,
                            mx: 'auto',
                        }}
                    >
                        {isPlanChange ? 'Selecione o novo plano' : 'Comece agora, cancele quando quiser'}
                    </Typography>
                </Box>

                {/* Plans Box */}
                <PlansBoxContainer>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 3,
                            color: 'text.primary',
                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                        }}
                    >
                        Selecione seu plano
                    </Typography>

                    {/* Plans List */}
                    <Box>
                        {plans.map((plan) => {
                            const isSelected = selectedPlan?.id === plan.id;
                            const isCurrentPlan = currentPlanId && plan.id === currentPlanId;
                            const isCancelledPlan = isCurrentPlan && !subscriptionActive;
                            const isYearlyPlan = plan.duration === 'year';
                            const savings = isYearlyPlan ? calculateSavings(plan.price, plan.price * 1.5) : null;

                            return (
                                <PlanCard
                                    key={plan.id}
                                    isSelected={isSelected}
                                    onClick={() => handlePlanSelect(plan)}
                                >
                                    {isCancelledPlan && (
                                        <CancelledBadge>
                                            CANCELADO
                                        </CancelledBadge>
                                    )}

                                    {!isCancelledPlan && isYearlyPlan && (
                                        <PromoBadge>
                                            PROMO
                                        </PromoBadge>
                                    )}

                                    <PlanLeftSection>
                                        <PlanRadio>
                                            {isSelected ? (
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: 24,
                                                        color: '#35e87c',
                                                    }}
                                                />
                                            ) : (
                                                <RadioButtonUncheckedIcon
                                                    sx={{
                                                        fontSize: 24,
                                                        color: theme.palette.text.secondary,
                                                    }}
                                                />
                                            )}
                                        </PlanRadio>

                                        <PlanInfo>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PlanName>
                                                    {plan.name}
                                                </PlanName>

                                                <InfoButton
                                                    onClick={(e) => handleOpenPlanDetails(plan, e)}
                                                    title="Ver detalhes do plano"
                                                >
                                                    <InfoIcon sx={{ fontSize: 20, fontWeight: 'bold' }} />
                                                </InfoButton>
                                            </Box>

                                            <PlanBadges>
                                                {savings && savings > 0 && (
                                                    <SaveBadge>
                                                        Economize {savings}%
                                                    </SaveBadge>
                                                )}
                                            </PlanBadges>
                                        </PlanInfo>
                                    </PlanLeftSection>

                                    <PriceSection>
                                        {plan.duration !== 'free' ? (
                                            <>
                                                <PriceRow>
                                                    {showDiscount && (
                                                        <OldPrice>
                                                            R$ {formatPrice(plan.price)}
                                                        </OldPrice>
                                                    )}
                                                    {!showDiscount && savings && savings > 0 && (
                                                        <OldPrice>
                                                            R$ {formatPrice(plan.price * 1.5)}
                                                        </OldPrice>
                                                    )}
                                                    <Price>
                                                        R$ {formatPrice(getPriceWithDiscount(plan.price))}
                                                    </Price>
                                                </PriceRow>
                                                {showDiscount && (
                                                    <SaveBadge sx={{ fontSize: '0.75rem', mb: 1 }}>
                                                        10% OFF
                                                    </SaveBadge>
                                                )}
                                                <PriceDescription>
                                                    Por mês, cobrado {plan.duration === 'year' ? 'anualmente' : 'mensalmente'}
                                                </PriceDescription>
                                            </>
                                        ) : (
                                            <Price>
                                                Grátis
                                            </Price>
                                        )}
                                    </PriceSection>
                                </PlanCard>
                            );
                        })}
                    </Box>

                    {/* Continue Button inside the box */}
                    {selectedPlan && (
                        <Box sx={{ mt: 3 }}>
                            <ContinueButton
                                fullWidth
                                onClick={handleContinue}
                                endIcon={<ArrowForward />}
                            >
                                Continuar
                            </ContinueButton>
                        </Box>
                    )}
                </PlansBoxContainer>

                {/* Info Section */}
                {selectedPlan && (
                    <ContinueSection>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: 'text.primary',
                            }}
                        >
                            Comece agora mesmo!
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    Acesso imediato
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    Cancele quando quiser
                                </Typography>
                            </Box>
                        </Box>
                    </ContinueSection>
                )}

                {!selectedPlan && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button
                            variant="text"
                            onClick={onBack}
                            sx={{
                                color: 'text.secondary',
                                textTransform: 'none',
                                fontWeight: 500,
                                '&:hover': {
                                    background: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.05)'
                                        : 'rgba(0, 0, 0, 0.03)',
                                },
                            }}
                        >
                            Voltar
                        </Button>
                    </Box>
                )}

                {/* Review Section */}
                <ReviewSection>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                mb: 1,
                            }}
                        >
                            Depoimento de um <span style={{ color: theme.palette.mode === 'dark' ? '#4ade80' : '#22c55e' }}>cliente</span>
                        </Typography>
                    </Box>

                    <ReviewCard>
                        <ReviewText>
                            Eu já testei vários sistemas de IA para conversas com clientes,
                            mas nenhum oferecia um CRM tão intuitivo e completo quanto a LIA TALK. A maioria das plataformas tinha interfaces
                            confusas e dificultavam a gestão dos leads e o acompanhamento das conversas. Com a LIA TALK, consegui centralizar
                            toda a comunicação com meus clientes de forma simples e eficiente. A integração com WhatsApp é perfeita, o sistema
                            de automação é poderoso, e o melhor de tudo: é extremamente fácil de usar. Finalmente encontrei uma solução que
                            realmente entende as necessidades de quem trabalha com atendimento ao cliente e vendas. Recomendo muito!
                        </ReviewText>

                        <ReviewAuthor>
                            <AuthorAvatar
                                src="/lucas-brito-depoimento.png"
                                alt="Profile"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }}
                            />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        color: theme.palette.mode === 'dark' ? '#4ade80' : '#22c55e',
                                        mb: 0.5,
                                    }}
                                >
                                    Lucas Brito
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    Analista de marketing digital
                                </Typography>
                            </Box>
                        </ReviewAuthor>
                    </ReviewCard>
                </ReviewSection>

                {/* Video Section */}
                <VideoSection>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                mb: 2,
                            }}
                        >
                            Veja Como Funciona
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 400,
                                maxWidth: 600,
                                mx: 'auto',
                            }}
                        >
                            Descubra como a LIA TALK pode transformar seu atendimento ao cliente
                        </Typography>
                    </Box>

                    <VideoContainer>
                        <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube.com/embed/5Sn4JqSRqGk?si=2tXBIuG-lYFhiMkK"
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%'
                            }}
                        />
                    </VideoContainer>
                </VideoSection>

                {/* Features Section */}
                <FeaturesSection>
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                }}
                            >
                                Nossos Recursos
                            </Typography>
                        </Box>

                        <Box sx={{
                            columns: { xs: 1, md: 2, lg: 3 },
                            gap: 2,
                        }}>
                            <FeatureCategory>
                                <FeatureCategoryHeader>
                                    <FeatureCategoryTitle>Gestão de Conversas</FeatureCategoryTitle>
                                </FeatureCategoryHeader>
                                <FeatureList>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>WhatsApp Integrado</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Respostas Automáticas</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Histórico Completo</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Múltiplos Atendentes</FeatureItemText>
                                    </FeatureItem>
                                </FeatureList>
                            </FeatureCategory>

                            <FeatureCategory>
                                <FeatureCategoryHeader>
                                    <FeatureCategoryTitle>Gestão de Leads</FeatureCategoryTitle>
                                </FeatureCategoryHeader>
                                <FeatureList>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Kanban Visual</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Qualificação de Leads</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Follow-up Automático</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Tags e Filtros</FeatureItemText>
                                    </FeatureItem>
                                </FeatureList>
                            </FeatureCategory>

                            <FeatureCategory>
                                <FeatureCategoryHeader>
                                    <FeatureCategoryTitle>Inteligência Artificial</FeatureCategoryTitle>
                                </FeatureCategoryHeader>
                                <FeatureList>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Assistente Virtual IA</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Análise de Sentimento</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Sugestões Inteligentes</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Automação Avançada</FeatureItemText>
                                    </FeatureItem>
                                </FeatureList>
                            </FeatureCategory>

                            <FeatureCategory>
                                <FeatureCategoryHeader>
                                    <FeatureCategoryTitle>Relatórios e Analytics</FeatureCategoryTitle>
                                </FeatureCategoryHeader>
                                <FeatureList>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Dashboard em Tempo Real</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Métricas de Performance</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Relatórios Personalizados</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Exportação de Dados</FeatureItemText>
                                    </FeatureItem>
                                </FeatureList>
                            </FeatureCategory>

                            <FeatureCategory>
                                <FeatureCategoryHeader>
                                    <FeatureCategoryTitle>Integrações</FeatureCategoryTitle>
                                </FeatureCategoryHeader>
                                <FeatureList>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>WhatsApp Business API</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>CRM Integration</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Zapier & Make</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>API REST</FeatureItemText>
                                    </FeatureItem>
                                </FeatureList>
                            </FeatureCategory>

                            <FeatureCategory>
                                <FeatureCategoryHeader>
                                    <FeatureCategoryTitle>Segurança e Suporte</FeatureCategoryTitle>
                                </FeatureCategoryHeader>
                                <FeatureList>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Criptografia End-to-End</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Backup Automático</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Suporte 24/7</FeatureItemText>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                        <FeatureItemText>Conformidade LGPD</FeatureItemText>
                                    </FeatureItem>
                                </FeatureList>
                            </FeatureCategory>
                        </Box>
                    </Container>
                </FeaturesSection>

                {/* Security Section */}
                <SecuritySection>
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                            <ShieldIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                }}
                            >
                                Pagamento 100% Seguro
                            </Typography>
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                                maxWidth: 600,
                                mx: 'auto',
                            }}
                        >
                            Processamos todos os pagamentos através da Asaas, garantindo máxima segurança para seus dados
                        </Typography>

                        <SecurityFeatures>
                            <SecurityFeature>
                                <LockIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Criptografia SSL
                                </Typography>
                            </SecurityFeature>

                            <SecurityFeature>
                                <VerifiedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Certificação PCI DSS
                                </Typography>
                            </SecurityFeature>

                            <SecurityFeature>
                                <SecurityIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Proteção LGPD
                                </Typography>
                            </SecurityFeature>
                        </SecurityFeatures>
                    </Box>
                </SecuritySection>

                {/* Payment Modal */}
                <Modal
                    open={paymentModalOpen}
                    onClose={handleClosePaymentModal}
                    aria-labelledby="payment-modal-title"
                >
                    <PaymentModalBox>
                        <PaymentModalHeader>
                            <Typography
                                id="payment-modal-title"
                                variant="h5"
                                sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                                Finalizar Pagamento
                            </Typography>
                            <CloseButton onClick={handleClosePaymentModal}>
                                <CloseIcon />
                            </CloseButton>
                        </PaymentModalHeader>

                        {/* Plan Summary */}
                        {selectedPlan && (
                            <PlanSummaryBox>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        Plano Selecionado
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {selectedPlan.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        Valor
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        {showDiscount && (
                                            <Typography variant="body2" sx={{
                                                color: 'text.secondary',
                                                textDecoration: 'line-through',
                                                fontSize: '0.875rem',
                                            }}>
                                                R$ {formatPrice(selectedPlan.price)}
                                            </Typography>
                                        )}
                                        <Typography variant="h5" sx={{
                                            fontWeight: 700,
                                            color: theme.palette.mode === 'dark' ? '#4ade80' : '#22c55e',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            R$ {formatPrice(getPriceWithDiscount(selectedPlan.price))}
                                            {showDiscount && (
                                                <SaveBadge sx={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                                                    10% OFF
                                                </SaveBadge>
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.5 }}>
                                    {selectedPlan.duration === 'year' ? 'por mês, cobrado anualmente' : 'por mês'}
                                </Typography>
                            </PlanSummaryBox>
                        )}

                        {/* Error Message */}
                        {paymentError && (
                            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPaymentError(null)}>
                                {paymentError}
                            </Alert>
                        )}

                        {/* Payment Form */}
                        <PaymentFormBox>
                            {/* Seleção de cartão salvo ou novo */}
                            {savedCards.length > 0 && (
                                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                                    <FormLabel component="legend">Forma de pagamento</FormLabel>
                                    <RadioGroup 
                                        value={useNewCard ? 'new' : 'saved'} 
                                        onChange={(e) => {
                                            const newValue = e.target.value === 'new';
                                            setUseNewCard(newValue);
                                            if (!newValue && savedCards.length > 0) {
                                                const defaultCard = savedCards.find((c: SavedCreditCard) => c.isDefault);
                                                setSelectedCardId(defaultCard ? defaultCard.id : savedCards[0].id);
                                            }
                                        }}
                                    >
                                        <FormControlLabel value="saved" control={<Radio />} label={`Usar cartão salvo (${savedCards.length} disponível${savedCards.length > 1 ? 'eis' : ''})`} />
                                        <FormControlLabel value="new" control={<Radio />} label="Usar novo cartão" />
                                    </RadioGroup>
                                </FormControl>
                            )}

                            {/* Lista de cartões salvos */}
                            {!useNewCard && savedCards.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <List>
                                        {savedCards.map((card) => (
                                            <ListItem
                                                key={card.id}
                                                secondaryAction={
                                                    <IconButton edge="end" onClick={() => handleDeleteCard(card.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                                sx={{
                                                    border: selectedCardId === card.id ? 2 : 1,
                                                    borderColor: selectedCardId === card.id ? 'primary.main' : 'divider',
                                                    borderRadius: 2,
                                                    mb: 1,
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => {
                                                    setSelectedCardId(card.id);
                                                    // Preencher automaticamente os dados do cliente se disponíveis
                                                    if (card.customerCpfCnpj || card.customerName || card.customerEmail) {
                                                        setCustomerIdentity(card.customerCpfCnpj || customerIdentity);
                                                        setCustomerName(card.customerName || customerName);
                                                        setCustomerEmail(card.customerEmail || customerEmail);
                                                    }
                                                }}
                                            >
                                                <Radio checked={selectedCardId === card.id} />
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography>{card.brand}</Typography>
                                                            <Typography>•••• {card.last4Digits}</Typography>
                                                            {card.isDefault && <Chip label="Padrão" size="small" color="primary" />}
                                                        </Box>
                                                    }
                                                    secondary={`${card.holderName} - Validade: ${card.expiryMonth}/${card.expiryYear}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {/* Formulário de novo cartão */}
                            {useNewCard && (
                                <>
                            <TextField
                                fullWidth
                                label="Número do Cartão"
                                value={cardNumber}
                                onChange={(e) => {
                                    const formatted = formatCardNumber(e.target.value);
                                    setCardNumber(formatted);
                                    if (paymentError) setPaymentError(null);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CreditCardIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                placeholder="1234 5678 9012 3456"
                                inputProps={{ maxLength: 19 }}
                                error={cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardNumber)}
                                helperText={cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardNumber) 
                                    ? 'Número do cartão inválido' 
                                    : ''}
                            />

                            <TextField
                                fullWidth
                                label="Nome do Titular (como está no cartão)"
                                value={cardHolder}
                                onChange={(e) => {
                                    setCardHolder(e.target.value.toUpperCase());
                                    if (paymentError) setPaymentError(null);
                                }}
                                placeholder="NOME COMPLETO"
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Validade"
                                    value={expirationDate}
                                    onChange={(e) => {
                                        const formatted = formatExpirationDate(e.target.value);
                                        setExpirationDate(formatted);
                                        if (paymentError) setPaymentError(null);
                                    }}
                                    placeholder="MM/AA"
                                    inputProps={{ maxLength: 5 }}
                                    error={expirationDate.length > 0 && !validateExpirationDate(expirationDate).valid}
                                    helperText={expirationDate.length > 0 && !validateExpirationDate(expirationDate).valid 
                                        ? validateExpirationDate(expirationDate).message 
                                        : 'Formato: MM/AA'}
                                />

                                <TextField
                                    fullWidth
                                    label="CVV"
                                    value={securityCode}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        setSecurityCode(value);
                                        if (paymentError) setPaymentError(null);
                                    }}
                                    placeholder="123"
                                    inputProps={{ maxLength: 4 }}
                                    type="password"
                                />
                            </Box>

                            <TextField
                                fullWidth
                                label="Nome Completo"
                                value={customerName}
                                onChange={(e) => {
                                    setCustomerName(e.target.value);
                                    if (paymentError) setPaymentError(null);
                                }}
                                placeholder="Seu nome completo"
                            />

                            <TextField
                                fullWidth
                                label="E-mail"
                                type="email"
                                value={customerEmail}
                                onChange={(e) => {
                                    setCustomerEmail(e.target.value);
                                    if (paymentError) setPaymentError(null);
                                }}
                                placeholder="seu@email.com"
                            />

                            <TextField
                                fullWidth
                                label="CPF"
                                value={customerIdentity}
                                onChange={(e) => {
                                    const formatted = formatCPF(e.target.value);
                                    setCustomerIdentity(formatted);
                                    if (paymentError) setPaymentError(null);
                                }}
                                placeholder="000.000.000-00"
                                inputProps={{ maxLength: 14 }}
                            />
                            </>
                            )}

                            {useNewCard && (
                                <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'info.light', borderRadius: 1 }}>
                                    <Typography variant="body2" color="black">
                                        💳 Este cartão será salvo automaticamente após o pagamento para uso em futuras transações
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 2,
                                mb: 3,
                                padding: 2,
                                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 2,
                            }}>
                                <LockIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Pagamento 100% seguro via Asaas
                                </Typography>
                            </Box>

                            <PayButton
                                onClick={handlePayment}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                                        Processando...
                                    </>
                                ) : (
                                    <>
                                        Finalizar Pagamento
                                        <ArrowForward sx={{ ml: 1 }} />
                                    </>
                                )}
                            </PayButton>
                        </PaymentFormBox>
                    </PaymentModalBox>
                </Modal>

                {/* Payment Loading Modal */}
                <PaymentLoadingModal
                    open={showPaymentLoading}
                    message={paymentLoadingMessage}
                />

                {/* Plan Details Modal */}
                <Modal
                    open={planDetailsModalOpen}
                    onClose={handleClosePlanDetails}
                    aria-labelledby="plan-details-modal-title"
                >
                    <PlanDetailsModalBox>
                        <PaymentModalHeader>
                            <Typography
                                id="plan-details-modal-title"
                                variant="h5"
                                sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                                {selectedPlanForDetails?.name}
                            </Typography>
                            <CloseButton onClick={handleClosePlanDetails}>
                                <CloseIcon />
                            </CloseButton>
                        </PaymentModalHeader>

                        {selectedPlanForDetails && (
                            <>
                                <PlanDetailsHeader>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 1,
                                        mb: 2,
                                        flexWrap: 'wrap',
                                    }}>
                                        <Typography variant="h3" sx={{
                                            fontWeight: 700,
                                            color: theme.palette.mode === 'dark' ? '#4ade80' : '#22c55e',
                                        }}>
                                            {selectedPlanForDetails.duration === 'free' ? 'Grátis' : `R$ ${formatPrice(getPriceWithDiscount(selectedPlanForDetails.price))}`}
                                        </Typography>
                                        {selectedPlanForDetails.duration !== 'free' && (
                                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                                por mês
                                            </Typography>
                                        )}
                                        {showDiscount && selectedPlanForDetails.duration !== 'free' && (
                                            <SaveBadge sx={{ fontSize: '0.75rem', ml: 1 }}>
                                                10% OFF
                                            </SaveBadge>
                                        )}
                                    </Box>

                                    {selectedPlanForDetails.description && (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 3,
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            {selectedPlanForDetails.description}
                                        </Typography>
                                    )}
                                </PlanDetailsHeader>

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 2,
                                        color: 'text.primary',
                                    }}
                                >
                                    O que está incluído:
                                </Typography>

                                <PlanFeaturesList>
                                    {getPlanFeatures(selectedPlanForDetails).map((feature, index) => (
                                        <PlanFeatureItem key={index}>
                                            <CheckCircleIcon sx={{
                                                fontSize: 24,
                                                color: '#22c55e',
                                                flexShrink: 0,
                                                mt: 0.25,
                                            }} />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: 'text.primary',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {feature}
                                            </Typography>
                                        </PlanFeatureItem>
                                    ))}
                                </PlanFeaturesList>

                                <Box sx={{ mt: 4 }}>
                                    <ContinueButton
                                        fullWidth
                                        onClick={() => {
                                            handleClosePlanDetails();
                                            handlePlanSelect(selectedPlanForDetails);
                                        }}
                                    >
                                        Selecionar este plano
                                        <ArrowForward sx={{ ml: 1 }} />
                                    </ContinueButton>
                                </Box>
                            </>
                        )}
                    </PlanDetailsModalBox>
                </Modal>
            </Container>
        </PageWrapper>
    );
};

export default PlanSelectionPage;
